/**
 * Portfolio page type definitions for the PDF flipbook
 */

/**
 * A standard single-PDF page
 */
export interface SinglePage {
  type: 'single';
  url: string;
}

/**
 * Layout options for unfoldable pages
 */
export type UnfoldLayout = '1x2' | '1x3' | '1x4' | '2x2';

/**
 * An unfoldable page that displays PDFs in a gatefold layout
 * First 2 PDFs from the pdfs array are shown in the flipbook
 */
export interface UnfoldPage {
  type: 'unfold';
  preview?: string; // DEPRECATED - kept for backwards compatibility
  pdfs: string[]; // First 2 shown in flipbook, all shown in gatefold
  layout: UnfoldLayout; // Layout specification
}

/**
 * Union type for all portfolio page types
 */
export type PortfolioPage = SinglePage | UnfoldPage;

/**
 * Type guard to check if a page is a single page
 */
export function isSinglePage(page: PortfolioPage): page is SinglePage {
  return page.type === 'single';
}

/**
 * Type guard to check if a page is an unfold page
 */
export function isUnfoldPage(page: PortfolioPage): page is UnfoldPage {
  return page.type === 'unfold';
}

/**
 * Validates that an unfold page has the correct number of PDFs for its layout
 */
export function validateUnfoldPage(page: UnfoldPage): boolean {
  const expectedCounts: Record<UnfoldLayout, number> = {
    '1x2': 2,
    '1x3': 3,
    '1x4': 4,
    '2x2': 4,
  };

  const expected = expectedCounts[page.layout];
  if (!page.pdfs || page.pdfs.length !== expected) {
    console.warn(`Unfold page with layout ${page.layout} has ${page.pdfs?.length ?? 0} PDFs, expected ${expected}`);
    return false;
  }
  return true;
}
